import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { convertNoticeFiltersToSearchRequest, NoticeFilters } from "components/notices/utils";
import { SaveToDrawer } from "components/opportunities/saving/SaveToDrawer";
import { Table } from "lib/core_components/Table";
import { NoticesAnalysisByGroupRequestSchema_SortOrderEnum_0 } from "lib/generated/app-service-gql/graphql";
import { useNoticesAnalysisByGroup } from "lib/hooks/api/notices/analysis/useNoticesAnalysisByGroup";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { NoticeAnalysisByGroupGroupBy, OpportunityEntityType } from "lib/types/graphQLEnums";
import { toLowercase } from "lib/utils/stringUtils";
import UIcon from "../../lib/icons/untitled_ui/UIcon";
import { InRowSaveToButton } from "../opportunities/saving/InRowSaveToButton";
import { convertEntityTypeToOpportunityEntityType } from "../opportunities/saving/types";
import EmptyAnalysis from "./EmptyAnalysis";
import HeadlineFigure from "./HeadlineFigure";
import NoticeAnalysisActionBar from "./NoticeAnalysisActionBar";
import ValueBar from "./ValueBar";

const enumToLabel = (enumValue: NoticeAnalysisByGroupGroupBy) => {
  switch (enumValue) {
    case NoticeAnalysisByGroupGroupBy.Buyer:
      return "Buyer";
    case NoticeAnalysisByGroupGroupBy.Supplier:
      return "Supplier";
  }
};

const enumToUrl = (enumValue: NoticeAnalysisByGroupGroupBy, guid: string) => {
  switch (enumValue) {
    case NoticeAnalysisByGroupGroupBy.Buyer:
      return `${routes.buyer}/${guid}`;
    case NoticeAnalysisByGroupGroupBy.Supplier:
      return `${routes.supplier}/${guid}`;
  }
};

const DEFAULT_PAGINATION = { current: 1, pageSize: 20 };

type ByGroupProps = {
  filters: NoticeFilters;
  groupBy?: NoticeAnalysisByGroupGroupBy;
  setFilters: (newValue: React.SetStateAction<NoticeFilters | undefined>) => void;
  selectedViewId?: string;
};

function ByGroup({
  groupBy = NoticeAnalysisByGroupGroupBy.Buyer,
  filters,
  setFilters,
  selectedViewId,
}: ByGroupProps) {
  const { logEvent } = useTracking();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data, isLoading, isError } = useNoticesAnalysisByGroup(
    convertNoticeFiltersToSearchRequest(filters),
    {
      groupBy,
      sortOrder: NoticesAnalysisByGroupRequestSchema_SortOrderEnum_0.Desc,
      limit: pagination.pageSize,
      page: pagination.current,
    },
  );
  const lowerCaseGroupBy = toLowercase(groupBy);

  // If group changes: reset the current page to 1 but keep pageSize the same
  useEffect(() => {
    setPagination((prev) => ({ current: 1, pageSize: prev.pageSize }));
  }, [groupBy]);

  if (data?.analysis?.byGroup?.results.length === 0) {
    return (
      <EmptyAnalysis filters={filters} setFilters={setFilters} selectedViewId={selectedViewId} />
    );
  }

  const handleSave = () => {
    setIsDrawerOpen(true);
  };

  const handleClear = () => {
    setSelectedRows([]);
  };

  return (
    <Wrapper column flexGrow={1} gap={24}>
      <Flex gap={24}>
        <HeadlineFigure
          label={enumToLabel(groupBy)}
          value={data?.analysis?.byGroup?.totalResults}
          valueType="number"
          isLoading={isLoading}
        />
        <HeadlineFigure
          label="Notice"
          value={data?.totalResults}
          valueType="number"
          isLoading={isLoading}
        />
        <HeadlineFigure
          label="Total Value"
          value={data?.analysis?.overall?.noticeValues}
          valueType="currency"
          isLoading={isLoading}
          noPluralise
        />
      </Flex>
      {selectedRows.length > 0 && (
        <NoticeAnalysisActionBar
          selectedRows={selectedRows}
          onSave={handleSave}
          onClear={handleClear}
        />
      )}
      <Table
        dataSource={data?.analysis?.byGroup?.results}
        rowKey="guid"
        isError={isError}
        loading={isLoading}
        tableLayout="fixed"
        columns={[
          {
            key: "name",
            title: enumToLabel(groupBy),
            render: (_: unknown, record) => (
              <NameCell>
                <a href={enumToUrl(groupBy, record.guid)}>{record.name}</a>
                {record.isSavedCount > 0 && (
                  <BookmarkIcon icon="bookmark" color="sysPrimaryDefault" size={12} />
                )}
                <InRowSaveToButton
                  displayType="button"
                  entityType={lowerCaseGroupBy}
                  entityIds={[record.guid]}
                  savedCount={record.isSavedCount}
                  defaultOpportunityValues={{
                    entities: [
                      {
                        entityId: record.guid,
                        entityType: convertEntityTypeToOpportunityEntityType(
                          lowerCaseGroupBy,
                          OpportunityEntityType,
                        ),
                      },
                    ],
                  }}
                />
              </NameCell>
            ),
          },
          {
            key: "noticeCount",
            title: "Notice count",
            render: (_: unknown, record) => (
              <ValueBar
                value={record.noticeCount}
                maxValue={data?.analysis?.byGroup?.maxNoticeCount}
                type="count"
              />
            ),
          },
          {
            key: "noticeValues",
            title: "Notice value",
            render: (_: unknown, record) => (
              <ValueBar
                value={record.noticeValues}
                maxValue={data?.analysis?.byGroup?.maxNoticeValues}
                type="currency"
              />
            ),
          },
        ]}
        rowSelection={{
          onChange: (selectedRowKeys) => {
            setSelectedRows(selectedRowKeys.map((key) => key.toString()));
          },
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedRows,
        }}
        pagination={{
          ...pagination,
          total: data?.analysis?.byGroup?.totalResults,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            logEvent(EventNames.paginationSelected, { Value: page });
          },
          showSizeChanger: true,
        }}
      />
      <SaveToDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        entityType={lowerCaseGroupBy}
        entityIds={selectedRows}
        savedCount={selectedRows.length}
        defaultOpportunityValues={{
          entities: selectedRows.map((row) => ({
            entityId: row,
            entityType: convertEntityTypeToOpportunityEntityType(
              lowerCaseGroupBy,
              OpportunityEntityType,
            ),
          })),
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Flex)({
  maxWidth: "100%",
});

const NameCell = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: "space-between",
  width: "100%",

  a: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },

  button: {
    visibility: "hidden",
    opacity: 0,
    flexShrink: 0,
  },

  "tr:hover &": {
    button: {
      visibility: "visible",
      opacity: 1,
    },
  },
});

const BookmarkIcon = styled(UIcon)({
  display: "flex",
  alignItems: "center",
  marginLeft: "8px",

  "tr:hover &": {
    visibility: "hidden",
    opacity: 0,
  },
});

export default ByGroup;
